import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AttachmentPanel from "@/components/AttachmentPanel";
import MentionTextarea, { renderWithMentions } from "@/components/MentionTextarea";
import IssueLinks from "@/components/IssueLinks";
import TimeTracking from "@/components/TimeTracking";
import RichTextEditor from "@/components/RichTextEditor";
import MarkdownRenderer from "@/components/MarkdownRenderer";
import { useRealtime } from "@/lib/realtime";
import { useCan } from "@/lib/perm";
import { useSettings, findType, findPriority, findStatus } from "@/lib/settings";
import { ISSUE_TYPES, PRIORITIES, STATUSES, initials } from "@/lib/meta";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Trash2, Send, ArrowLeft, Share2, ExternalLink, Check, Copy, Eye, EyeOff, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function IssueDetailPage() {
  const { issueKey } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { users, projects } = useApp();
  const canDelete = useCan("issue.delete");
  const s = useSettings() || {};
  const TYPES = (s.issueTypes && s.issueTypes.length) ? s.issueTypes : ISSUE_TYPES;
  const PRIOS = (s.priorities && s.priorities.length) ? s.priorities : PRIORITIES;
  const STATS = (s.statuses && s.statuses.length) ? s.statuses : STATUSES;

  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [watchersData, setWatchersData] = useState({ watchers: [], is_watching: false, count: 0 });
  const [comment, setComment] = useState("");
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [copied, setCopied] = useState(false);
  const [addingWatcher, setAddingWatcher] = useState(false);

  const load = async () => {
    if (!issueKey) return;
    try {
      const i = await api.get(`/issues/${issueKey}`);
      const issueData = i.data;
      setIssue(issueData);
      setDescDraft(issueData.description || "");
      setSummaryDraft(issueData.summary || "");

      const [c, a, w] = await Promise.all([
        api.get(`/issues/${issueData.id}/comments`),
        api.get(`/issues/${issueData.id}/activity`),
        api.get(`/issues/${issueData.id}/watchers`).catch(() => ({ data: { watchers: [], is_watching: false, count: 0 } })),
      ]);
      setComments(c.data || []);
      setActivity(a.data || []);
      setWatchersData(w.data || { watchers: [], is_watching: false, count: 0 });
    } catch (err) {
      console.error("Failed to load issue:", err);
      toast.error(err?.response?.data?.detail || "Issue not found");
      navigate("/issues");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
  }, [issueKey]);

  useRealtime((ev) => {
    if (!issue) return;
    if ((ev.type === "comment.new" || ev.type === "comment.deleted") && ev.issue_id === issue.id) { load(); }
    else if ((ev.type === "issue.updated" || ev.type === "issue.deleted") && ev.issue_id === issue.id) {
      if (ev.type === "issue.deleted") navigate("/issues"); else load();
    }
  }, [issue?.id]);

  const patch = async (upd) => {
    if (!issue) return;
    const r = await api.patch(`/issues/${issue.id}`, upd);
    setIssue(r.data);
    const a = await api.get(`/issues/${issue.id}/activity`);
    setActivity(a.data || []);
    toast.success("Updated issue");
  };

  const toggleWatch = async () => {
    if (!issue || !user) return;
    try {
      if (watchersData.is_watching) {
        const res = await api.delete(`/issues/${issue.id}/watchers/${user.id}`);
        setWatchersData(prev => ({
          ...prev,
          is_watching: false,
          watchers: res.data.watchers || prev.watchers.filter(w => w.id !== user.id),
          count: res.data.count ?? Math.max(0, prev.count - 1),
        }));
        toast.success("Stopped watching issue");
      } else {
        const res = await api.post(`/issues/${issue.id}/watchers`, { user_id: user.id });
        setWatchersData(prev => ({
          ...prev,
          is_watching: true,
          watchers: res.data.watchers || [...prev.watchers, user],
          count: res.data.count ?? (prev.count + 1),
        }));
        toast.success("You are now watching this issue");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update watchers");
    }
  };

  const addWatcher = async (targetUserId) => {
    if (!issue || !targetUserId) return;
    try {
      const res = await api.post(`/issues/${issue.id}/watchers`, { user_id: targetUserId });
      setWatchersData(prev => ({
        ...prev,
        watchers: res.data.watchers,
        is_watching: targetUserId === user?.id ? true : prev.is_watching,
        count: res.data.count,
      }));
      setAddingWatcher(false);
      toast.success("Watcher added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add watcher");
    }
  };

  const removeWatcher = async (targetUserId) => {
    if (!issue || !targetUserId) return;
    try {
      const res = await api.delete(`/issues/${issue.id}/watchers/${targetUserId}`);
      setWatchersData(prev => ({
        ...prev,
        watchers: res.data.watchers,
        is_watching: targetUserId === user?.id ? false : prev.is_watching,
        count: res.data.count,
      }));
      toast.success("Watcher removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove watcher");
    }
  };

  const addComment = async () => {
    if (!comment.trim() || !issue) return;
    await api.post(`/issues/${issue.id}/comments`, { body: comment });
    setComment("");
    load();
  };

  const deleteComment = async (cid) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await api.delete(`/comments/${cid}`);
      toast.success("Comment deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete comment");
    }
  };

  const del = async () => {
    if (!issue || !window.confirm(`Delete ${issue.key}?`)) return;
    await api.delete(`/issues/${issue.id}`);
    toast.success("Issue deleted");
    window.dispatchEvent(new Event("ft:issues-changed"));
    navigate("/issues");
  };

  const copyIssueLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success("Copied link to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Loading issue details…</div>;
  }

  if (!issue) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Issue not found</div>;
  }

  const proj = projects.find(p => p.id === issue.project_id);
  const assignee = users.find(u => u.id === issue.assignee_id);
  const reporter = users.find(u => u.id === issue.reporter_id);
  const T = findType(TYPES, issue.issue_type);

  return (
    <div className="flex flex-col h-full bg-background overflow-y-auto">
      {/* Top Header Breadcrumb & Toolbar */}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur px-6 py-3 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1 text-xs">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Button>

        <div className="h-4 w-px bg-border mx-1" />

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {proj && (
            <>
              <Link to={`/projects/${proj.id}`} className="hover:underline font-medium text-foreground">
                {proj.name} ({proj.key})
              </Link>
              <span>/</span>
            </>
          )}
          {T && <T.icon className={cn("w-4 h-4", T.cls)} />}
          <span className="font-mono font-semibold text-foreground">{issue.key}</span>
        </div>

        <div className="flex-1" />

        <Button
          variant={watchersData.is_watching ? "secondary" : "outline"}
          size="sm"
          onClick={toggleWatch}
          className={cn("gap-1.5 text-xs transition-colors", watchersData.is_watching && "bg-primary/10 text-primary border-primary/30 hover:bg-primary/20")}
          title={watchersData.is_watching ? "You are watching this issue (receiving emails)" : "Watch this issue to receive email notifications"}
          data-testid="toggle-watch-btn"
        >
          {watchersData.is_watching ? (
            <>
              <Eye className="w-3.5 h-3.5 text-primary" />
              <span className="font-medium">Watching ({watchersData.count || 0})</span>
            </>
          ) : (
            <>
              <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />
              <span>Watch ({watchersData.count || 0})</span>
            </>
          )}
        </Button>

        <Button variant="outline" size="sm" onClick={copyIssueLink} className="gap-1.5 text-xs">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? "Copied Link" : "Copy Link"}</span>
        </Button>

        {canDelete && (
          <Button variant="ghost" size="icon" onClick={del} className="h-8 w-8 text-muted-foreground hover:text-red-500" title="Delete issue">
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Main Full-Screen Layout Workspace */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-8 p-6 lg:p-8 max-w-7xl mx-auto w-full">
        {/* Left 2 Columns: Summary, Description, Attachments, Links, Time, Comments */}
        <div className="lg:col-span-2 space-y-6">
          {/* Summary Input / Display */}
          <div>
            {editingSummary ? (
              <Input
                value={summaryDraft}
                autoFocus
                onChange={(e) => setSummaryDraft(e.target.value)}
                onBlur={async () => {
                  if (summaryDraft !== issue.summary) await patch({ summary: summaryDraft });
                  setEditingSummary(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                  if (e.key === "Escape") { setSummaryDraft(issue.summary); setEditingSummary(false); }
                }}
                className="text-2xl font-display font-bold tracking-tight h-12"
              />
            ) : (
              <h1
                onClick={() => setEditingSummary(true)}
                className="text-2xl font-display font-bold tracking-tight cursor-pointer hover:bg-accent/40 rounded px-2 -mx-2 py-1 transition-colors"
                title="Click to edit summary"
              >
                {issue.summary}
              </h1>
            )}
          </div>

          {/* Description Section */}
          <div className="space-y-2 border-t pt-4">
            <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <span>Description</span>
              {!editingDesc && (
                <Button variant="ghost" size="xs" onClick={() => setEditingDesc(true)}>
                  Edit
                </Button>
              )}
            </div>

            {editingDesc ? (
              <div className="space-y-3">
                <RichTextEditor
                  value={descDraft}
                  onChange={setDescDraft}
                  issueId={issue.id}
                  users={users}
                  minRows={8}
                  testid="issue-desc-editor"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={async () => { await patch({ description: descDraft }); setEditingDesc(false); }}>
                    Save Description
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setDescDraft(issue.description || ""); setEditingDesc(false); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => setEditingDesc(true)}
                className="min-h-[120px] p-4 rounded-xl border border-border/60 hover:border-border hover:bg-accent/30 cursor-text transition-colors bg-background shadow-2xs"
              >
                {issue.description ? (
                  <MarkdownRenderer content={issue.description} users={users} />
                ) : (
                  <span className="text-sm text-muted-foreground italic">Add a description… (supports rich text, images, tables, code blocks)</span>
                )}
              </div>
            )}
          </div>

          {/* Attachments Section */}
          <div className="space-y-2 border-t pt-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attachments</div>
            <AttachmentPanel issueId={issue.id} />
          </div>

          {/* Linked Issues Section */}
          <div className="space-y-2 border-t pt-4">
            <IssueLinks issueId={issue.id} />
          </div>

          {/* Time Tracking Section */}
          <div className="space-y-2 border-t pt-4">
            <TimeTracking issue={issue} onIssuePatched={load} />
          </div>

          {/* Comments and Activity Tabs Section */}
          <div className="border-t pt-4">
            <Tabs defaultValue="comments">
              <TabsList className="mb-3">
                <TabsTrigger value="comments">Comments ({comments.length})</TabsTrigger>
                <TabsTrigger value="activity">Activity ({activity.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="comments" className="space-y-4 pt-2">
                {comments.map((c) => {
                  const author = users.find((u) => u.id === c.author_id);
                  const canDeleteComment = user && (user.id === c.author_id || user.role === "admin" || user.role === "owner");
                  return (
                    <div key={c.id} className="group relative flex gap-3 p-3 rounded-lg border bg-muted/20 hover:bg-muted/30 transition-colors">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={author?.avatar_url} />
                        <AvatarFallback className="text-[10px]">{initials(author?.name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="text-xs">
                            <span className="font-semibold text-foreground">{author?.name || "Unknown"}</span>
                            <span className="text-muted-foreground"> · {new Date(c.created_at).toLocaleString()}</span>
                          </div>
                          {canDeleteComment && (
                            <button
                              type="button"
                              onClick={() => deleteComment(c.id)}
                              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all p-1 rounded hover:bg-destructive/10 -mt-1 -mr-1"
                              title="Delete comment"
                              data-testid={`delete-comment-${c.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="text-sm mt-1 leading-relaxed">
                          <MarkdownRenderer content={c.body} users={users} />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="space-y-2 pt-2">
                  <RichTextEditor
                    value={comment}
                    onChange={setComment}
                    issueId={issue.id}
                    users={users}
                    placeholder="Add a comment… (paste or drag & drop images, format text, / commands, @ mentions)"
                    minRows={3}
                    testid="comment-input"
                  />
                  <div className="flex justify-end">
                    <Button size="sm" onClick={addComment} data-testid="comment-submit" className="gap-1.5">
                      <Send className="w-3.5 h-3.5" /> Save Comment
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="activity" className="pt-2 space-y-2">
                {activity.map((a) => {
                  const u = users.find((x) => x.id === a.user_id);
                  return (
                    <div key={a.id} className="text-xs text-muted-foreground flex gap-2 p-2 border-b border-border/40">
                      <span className="text-foreground font-medium">{u?.name || "Someone"}</span>
                      <span>{a.action}{a.field ? ` ${a.field}` : ""}{a.from_val && a.to_val ? `: ${a.from_val} → ${a.to_val}` : ""}</span>
                      <span className="ml-auto text-[10px]">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                  );
                })}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Right Column: Metadata Sidebar Fields */}
        <div className="space-y-6">
          <div className="p-5 border rounded-xl bg-card space-y-5 shadow-xs">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground border-b pb-2">Details</div>

            <Field label="Status">
              <Select value={issue.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger data-testid="issue-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATS.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.label || s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Assignee">
              <Select value={issue.assignee_id || "unassigned"} onValueChange={(v) => patch({ assignee_id: v === "unassigned" ? null : v })}>
                <SelectTrigger data-testid="issue-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="w-4 h-4">
                          <AvatarImage src={u.avatar_url} />
                          <AvatarFallback className="text-[8px]">{initials(u.name)}</AvatarFallback>
                        </Avatar>
                        <span>{u.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Priority">
              <Select value={issue.priority} onValueChange={(v) => patch({ priority: v })}>
                <SelectTrigger data-testid="issue-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIOS.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center gap-2">
                        <p.icon className={cn("w-4 h-4", p.cls)} />
                        <span>{p.label || p.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Issue Type">
              <Select value={issue.issue_type} onValueChange={(v) => patch({ issue_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex items-center gap-2">
                        <t.icon className={cn("w-4 h-4", t.cls)} />
                        <span>{t.label || t.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Story Points">
              <Input
                type="number"
                min="0"
                defaultValue={issue.story_points || ""}
                onBlur={(e) => patch({ story_points: e.target.value ? Number(e.target.value) : null })}
              />
            </Field>

            {issue.issue_type === "epic" && (
              <>
                <Field label="Start Date">
                  <Input
                    type="date"
                    defaultValue={issue.start_date ? issue.start_date.slice(0, 10) : ""}
                    onBlur={(e) => patch({ start_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  />
                </Field>
                <Field label="Target Date">
                  <Input
                    type="date"
                    defaultValue={issue.target_date ? issue.target_date.slice(0, 10) : ""}
                    onBlur={(e) => patch({ target_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  />
                </Field>
              </>
            )}

            <Field label="Due Date">
              <Input
                type="date"
                defaultValue={issue.due_date ? issue.due_date.slice(0, 10) : ""}
                onBlur={(e) => patch({ due_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
              />
            </Field>

            <Field label="Project">
              <div className="text-sm font-medium">{proj?.name || "—"}</div>
            </Field>

            <Field label="Reporter">
              <div className="text-sm font-medium">{reporter?.name || "—"}</div>
            </Field>

            <Field label={`Watchers (${watchersData.count || 0})`}>
              <div className="space-y-2 mt-1">
                <div className="flex flex-wrap gap-1.5 items-center">
                  {(watchersData.watchers || []).map((w) => (
                    <div
                      key={w.id}
                      className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full bg-muted/60 border text-xs group"
                      title={w.email}
                    >
                      <Avatar className="w-4 h-4">
                        <AvatarImage src={w.avatar_url} />
                        <AvatarFallback className="text-[8px]">{initials(w.name)}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-foreground truncate max-w-[100px]">{w.name}</span>
                      {(user?.id === w.id || user?.role === "admin" || user?.role === "owner") && (
                        <button
                          type="button"
                          onClick={() => removeWatcher(w.id)}
                          className="text-muted-foreground hover:text-destructive opacity-70 hover:opacity-100 ml-0.5"
                          title="Remove watcher"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  {(!watchersData.watchers || watchersData.watchers.length === 0) && (
                    <span className="text-xs text-muted-foreground italic">No watchers yet</span>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <Select onValueChange={(val) => { if (val) addWatcher(val); }}>
                    <SelectTrigger className="h-7 text-xs flex-1">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <UserPlus className="w-3 h-3" />
                        <span>Add watcher…</span>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {users
                        .filter((u) => !(watchersData.watchers || []).some((w) => w.id === u.id))
                        .map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            <div className="flex items-center gap-2">
                              <Avatar className="w-4 h-4">
                                <AvatarImage src={u.avatar_url} />
                                <AvatarFallback className="text-[8px]">{initials(u.name)}</AvatarFallback>
                              </Avatar>
                              <span>{u.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Field>

            <Field label="Created">
              <div className="text-xs text-muted-foreground">{new Date(issue.created_at).toLocaleString()}</div>
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      {children}
    </div>
  );
}
